import React, { useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  StyleProp,
  ViewStyle,
  TextStyle,
  NativeSyntheticEvent,
  TextInputFocusEventData,
} from 'react-native';
import { Text, TextInput, TextInputProps } from '@/components/common/text';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';

export interface GlassFormInputProps extends Omit<TextInputProps, 'style'> {
  label?: string;
  error?: string;
  required?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<TextStyle>;
  labelStyle?: StyleProp<TextStyle>;
}

export const GlassFormInput: React.FC<GlassFormInputProps> = ({
  label,
  error,
  required = false,
  containerStyle,
  inputStyle,
  labelStyle,
  multiline,
  onFocus,
  onBlur,
  ...rest
}) => {
  const styles = useStyles();
  const [isFocused, setIsFocused] = useState(false);
  // `field` kind: the input border moves to card.borderHover while the wrapper
  // is hovered; focus and error styles come later in the array and win.
  const { mouseProps, viewStyle: hoverViewStyle } = useHoverStyle('field');

  const handleFocus = useCallback(
    (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur],
  );

  return (
    <View style={[styles.container, containerStyle]} {...(mouseProps as any)}>
      {label ? (
        <Text style={[styles.label, labelStyle]}>
          {label}
          {required ? <Text style={styles.required}> *</Text> : null}
        </Text>
      ) : null}
      <TextInput
        style={[
          styles.input,
          hoverViewStyle,
          isFocused && !error && styles.inputFocused,
          error && styles.inputError,
          multiline && styles.textArea,
          inputStyle,
        ]}
        placeholderTextColor={styles.placeholder.color as string}
        multiline={multiline}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...rest}
      />
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
    </View>
  );
};

GlassFormInput.displayName = 'GlassFormInput';

const useStyles = () =>
  useThemedStyles(({ components, semantic }) =>
    StyleSheet.create({
      container: {
        marginBottom: 16,
      },
      label: {
        fontSize: 14,
        fontWeight: '500',
        color: components.admin.formInput.label,
        marginBottom: 8,
      },
      required: {
        color: semantic.status.error,
      },
      input: {
        backgroundColor: components.admin.formInput.bg,
        borderRadius: 8,
        paddingHorizontal: 16,
        paddingVertical: 12,
        fontSize: 16,
        color: components.admin.formInput.text,
        borderWidth: 1,
        borderColor: components.admin.formInput.border,
        minHeight: 48,
      },
      inputFocused: {
        borderColor: components.admin.formInput.borderFocused,
      },
      inputError: {
        borderColor: semantic.status.error,
      },
      textArea: {
        minHeight: 120,
        textAlignVertical: 'top',
        paddingTop: 12,
      },
      placeholder: {
        color: components.admin.formInput.placeholder,
      },
      errorText: {
        fontSize: 12,
        color: semantic.status.error,
        marginTop: 4,
      },
    }),
  );
